import { useId, useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import { IconSearch } from "@tabler/icons-react";
import { Switch } from "@/components/Switch/Switch";
import {
  ASSIGNABLE_CAPABILITY_CATALOGUE,
  CAPABILITY_CATALOGUE,
  CUSTOM_FOUNDATION_CAPABILITIES,
  type Capability,
  type CapabilityDescriptor,
  type CapabilityGroup,
  expandImplied,
  validateCapabilitySet,
} from "@/types/Capability";

/** The description key of a capability: its label key + `Desc` (`capabilities.guests.read` → `…readDesc`). */
export function capabilityDescriptionKey(desc: Pick<CapabilityDescriptor, "i18nKey">): string {
  return `${desc.i18nKey}Desc`;
}

/**
 * The ticked capability (catalogue order) that switches `cap` on with it, or
 * null when `cap` stands on its own. Such a capability cannot be switched off
 * alone: saving expands the implied closure, so it would come straight back.
 */
export function includedBy(cap: Capability, value: ReadonlySet<Capability>): Capability | null {
  if (!value.has(cap)) return null;
  for (const desc of CAPABILITY_CATALOGUE) {
    if (desc.key === cap || !value.has(desc.key)) continue;
    if (expandImplied(new Set([desc.key])).has(cap)) return desc.key;
  }
  return null;
}

/**
 * The role editor's capability picker (members plan B8), Cloudflare-style:
 * a search over name and description, one card per group with its count
 * ("Guests 2/9") and a switch for the whole group, and per capability a row
 * "name | what it lets you do | switch". A capability another ticked one
 * needs is on and locked ("Included with …"). Soft warnings stay on the row:
 * a delete without its write (`validateCapabilitySet`) and, fed by the
 * editor, an action whose field a restricted allowlist withholds.
 *
 * A capability the backend gives every custom role anyway
 * (`CUSTOM_FOUNDATION_CAPABILITIES`) is shown on and locked too ("Every custom
 * role has this") — it cannot be taken away. Locked switches stay focusable
 * (`aria-disabled`), so the reason linked to them is reachable. `disabled`
 * (a read-only editor) turns every switch and group control off.
 *
 * Each group header keeps the id `cap-group-<group>` — the editor scrolls the
 * slotting group into view when it grants Slotting access.
 * (`CapabilityCatalogueGrid` stays for the role-preview bar.)
 */
export function CapabilitySwitchList({
  value,
  onChange,
  fieldPrerequisiteWarnings,
  disabled = false,
}: {
  value: Set<Capability>;
  onChange: (next: Set<Capability>) => void;
  /** Capability → display labels of the fields its action needs but the allowlist lacks. */
  fieldPrerequisiteWarnings?: ReadonlyMap<Capability, string[]>;
  /** Read-only: every switch and group control shows its state and does nothing. */
  disabled?: boolean;
}) {
  const t = useTranslations();
  const tR = useTranslations("roleTemplates");
  const searchId = useId();
  const [search, setSearch] = useState("");

  const groups = useMemo(() => {
    const m = new Map<CapabilityGroup, CapabilityDescriptor[]>();
    for (const desc of ASSIGNABLE_CAPABILITY_CATALOGUE) {
      const list = m.get(desc.group) ?? [];
      list.push(desc);
      m.set(desc.group, list);
    }
    return Array.from(m.entries());
  }, []);

  const labelOf = (cap: Capability): string =>
    t(CAPABILITY_CATALOGUE.find((d) => d.key === cap)?.i18nKey ?? cap, { defaultValue: cap });
  const descriptionOf = (desc: CapabilityDescriptor): string =>
    t(capabilityDescriptionKey(desc), { defaultValue: "" });
  const groupLabelOf = (group: CapabilityGroup): string =>
    t(`capabilities.${group}._group`, { defaultValue: group });

  const warnings = useMemo(() => validateCapabilitySet(value), [value]);
  const warningsByCap = useMemo(() => {
    const m = new Map<Capability, string[]>();
    for (const w of warnings) {
      m.set(w.cap, [...(m.get(w.cap) ?? []), labelOf(w.missing)]);
    }
    if (fieldPrerequisiteWarnings) {
      for (const [cap, labels] of fieldPrerequisiteWarnings) {
        if (labels.length === 0) continue;
        m.set(cap, [...(m.get(cap) ?? []), ...labels]);
      }
    }
    return m;
    // labelOf reads the translator only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warnings, fieldPrerequisiteWarnings, t]);

  /** On as the role will have it: ticked, or given to every custom role anyway. */
  const isOn = (cap: Capability) => value.has(cap) || CUSTOM_FOUNDATION_CAPABILITIES.has(cap);

  const toggle = (cap: Capability) => {
    if (disabled) return;
    const next = new Set(value);
    if (next.has(cap)) next.delete(cap);
    else next.add(cap);
    // The implied closure, as the grid did: the saved set always satisfies the
    // dependency graph.
    onChange(expandImplied(next));
  };

  // The group switch: on when every capability of the group is on; switching
  // it flips the whole group (what other groups still need comes back with
  // the closure).
  const toggleGroup = (descs: CapabilityDescriptor[]) => {
    if (disabled) return;
    const allOn = descs.every((d) => isOn(d.key));
    const next = new Set(value);
    for (const d of descs) {
      // The foundation capability is not the editor's to give or take.
      if (CUSTOM_FOUNDATION_CAPABILITIES.has(d.key)) continue;
      if (allOn) next.delete(d.key);
      else next.add(d.key);
    }
    onChange(expandImplied(next));
  };

  const query = search.trim().toLocaleLowerCase();
  const matches = (desc: CapabilityDescriptor, groupLabel: string): boolean =>
    !query ||
    groupLabel.toLocaleLowerCase().includes(query) ||
    labelOf(desc.key).toLocaleLowerCase().includes(query) ||
    descriptionOf(desc).toLocaleLowerCase().includes(query) ||
    desc.key.includes(query);

  const visible = groups
    .map(([group, descs]) => {
      const groupLabel = groupLabelOf(group);
      return { group, descs, groupLabel, rows: descs.filter((d) => matches(d, groupLabel)) };
    })
    .filter((g) => g.rows.length > 0);

  return (
    <div className="flex flex-col gap-4" data-testid="capability-switch-list">
      <label className="relative block w-full sm:max-w-96">
        <span id={searchId} className="sr-only">{tR("capabilitySearch")}</span>
        <IconSearch size={16} aria-hidden className="absolute left-3 top-1/2 -translate-y-1/2 font-tertiary" />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={tR("capabilitySearch")}
          aria-labelledby={searchId}
          className="w-full h-9 rounded-lg border outline-tertiary surface-primary pl-9 pr-3 body-3-regular font-primary"
          data-testid="capability-search"
        />
      </label>

      {visible.length === 0 && (
        <p className="body-3-regular font-tertiary" data-testid="capability-no-match">
          {tR("capabilityNoMatch")}
        </p>
      )}

      {visible.map(({ group, descs, groupLabel, rows }) => {
        const selected = descs.filter((d) => isOn(d.key)).length;
        const headingId = `cap-group-${group}`;
        const countId = `cap-group-count-${group}`;
        return (
          <div
            key={group}
            role="group"
            aria-labelledby={headingId}
            className="rounded-xl border outline-quaternary surface-primary"
            data-testid={`capability-group-${group}`}
          >
            <div className="flex items-center justify-between gap-3 px-4 py-2.5 rounded-t-xl surface-secondary border-b outline-quaternary">
              <div className="flex items-center gap-2 min-w-0">
                <h3 id={headingId} className="body-2-bold font-secondary truncate">
                  {groupLabel}
                </h3>
                <span
                  aria-hidden
                  className="shrink-0 text-xs font-tertiary tabular-nums border outline-quaternary rounded-full px-2 py-0.5"
                  data-testid={`capability-group-count-${group}`}
                >
                  {selected}/{descs.length}
                </span>
                <span id={countId} className="sr-only">{tR("groupCount", { selected, total: descs.length })}</span>
              </div>
              {/* A tri-state checkbox (ARIA has no "mixed" switch) that looks like the switches. */}
              <Switch
                role="checkbox"
                checked={selected === descs.length ? true : selected === 0 ? false : "mixed"}
                onCheckedChange={() => toggleGroup(descs)}
                disabled={disabled}
                focusableWhenDisabled
                ariaLabel={tR("groupSwitch", { group: groupLabel })}
                ariaDescribedBy={countId}
                testId={`capability-group-switch-${group}`}
              />
            </div>
            <ul className="flex flex-col divide-y divide-(--color-outline-quaternary)">
              {rows.map((desc) => (
                <CapabilityRow
                  key={desc.key}
                  label={labelOf(desc.key)}
                  description={descriptionOf(desc)}
                  checked={isOn(desc.key)}
                  lockedBecause={(() => {
                    if (CUSTOM_FOUNDATION_CAPABILITIES.has(desc.key)) return tR("alwaysOn");
                    const by = includedBy(desc.key, value);
                    return by ? tR("includedWith", { capability: labelOf(by) }) : null;
                  })()}
                  needs={warningsByCap.get(desc.key) ?? null}
                  disabled={disabled}
                  onToggle={() => toggle(desc.key)}
                  testId={`capability-switch-${desc.key}`}
                />
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

function CapabilityRow({
  label,
  description,
  checked,
  lockedBecause,
  needs,
  disabled,
  onToggle,
  testId,
}: {
  label: string;
  description: string;
  checked: boolean;
  /** Why the switch is on and locked ("Included with …", "Every custom role has this."). */
  lockedBecause: string | null;
  /** What it still needs (missing capability / field labels). */
  needs: string[] | null;
  /** The whole list is read-only. */
  disabled: boolean;
  onToggle: () => void;
  testId: string;
}) {
  const tR = useTranslations("roleTemplates");
  const baseId = useId();
  const nameId = `${baseId}-name`;
  const descId = `${baseId}-desc`;
  const lockId = `${baseId}-lock`;
  const needsId = `${baseId}-needs`;
  const describedBy = [description ? descId : null, lockedBecause ? lockId : null, needs ? needsId : null]
    .filter(Boolean)
    .join(" ");
  return (
    <li className={`flex items-start gap-3 px-4 py-3 ${checked ? "surface-secondary" : ""}`}>
      <div className="min-w-0 flex-1 flex flex-col sm:flex-row sm:items-start gap-0.5 sm:gap-4">
        <span id={nameId} className="sm:w-56 sm:shrink-0 body-3-medium font-primary wrap-anywhere">
          {label}
        </span>
        <div className="min-w-0 flex-1 flex flex-col gap-0.5">
          {description && (
            <span id={descId} className="body-3-regular font-tertiary">
              {description}
            </span>
          )}
          {lockedBecause && (
            <span id={lockId} className="body-4-regular font-tertiary">
              {lockedBecause}
            </span>
          )}
          {needs && (
            <span id={needsId} className="body-4-regular font-error">
              ⚠ {tR("alsoNeeds", { items: needs.join(", ") })}
            </span>
          )}
        </div>
      </div>
      <span className="shrink-0 pt-0.5">
        <Switch
          checked={checked}
          disabled={disabled || !!lockedBecause}
          focusableWhenDisabled
          onCheckedChange={onToggle}
          ariaLabelledBy={nameId}
          ariaDescribedBy={describedBy || undefined}
          testId={testId}
        />
      </span>
    </li>
  );
}
